import { FAVORITE_CATEGORIES } from "./categories.mjs";

// Format wie im BMTG-Theme: { version: 2, items: [{ id, title, url, … }] }.
// Weißenstadt hatte keine Vorgängerliste, deshalb gibt es keinen Legacy-Schlüssel.
export const STORAGE_KEY = "weissenstadt-favorites";

export function safeUrl(value, origin) {
  try {
    if (typeof value !== "string" || !value.trim()) return "";
    const url = new URL(value, origin);
    return /^https?:$/.test(url.protocol) &&
      url.origin === origin &&
      !url.username &&
      !url.password
      ? url.href
      : "";
  } catch {
    return "";
  }
}

/** Opening hours as sent by the REST route; never stored, only displayed. */
function cleanOpening(opening) {
  if (!opening || typeof opening !== "object") return null;
  const periods = Array.isArray(opening.periods)
    ? opening.periods
        .filter(
          (period) =>
            Array.isArray(period) &&
            Number.isInteger(period[0]) &&
            period[0] >= 0 &&
            period[0] <= 6 &&
            typeof period[1] === "string" &&
            typeof period[2] === "string",
        )
        .slice(0, 50)
        .map(([day, open, close]) => [day, open.slice(0, 5), close.slice(0, 5)])
    : [];
  const special = Array.isArray(opening.special)
    ? opening.special
        .filter((row) => row && typeof row === "object")
        .slice(0, 50)
    : [];
  return periods.length || special.length ? { periods, special } : null;
}

/** Drops invalid entries; display details (type, meta, opening) only when asked for. */
export function normalizeItems(items, origin, withDetails = false) {
  if (!Array.isArray(items)) throw new Error("corrupt");
  const seen = new Set();
  return items.flatMap((item) => {
    const id = Number(item?.id);
    const url = typeof item?.url === "string" ? safeUrl(item.url, origin) : "";
    if (
      !Number.isSafeInteger(id) ||
      id < 1 ||
      seen.has(id) ||
      !url ||
      typeof item?.title !== "string"
    )
      return [];
    seen.add(id);
    const clean = { id, title: item.title.slice(0, 500), url };
    if (FAVORITE_CATEGORIES.includes(item.category))
      clean.category = item.category;
    for (const field of withDetails ? ["type", "meta", "credit"] : ["credit"]) {
      if (typeof item[field] === "string")
        clean[field] = item[field].slice(0, 500);
    }
    if (typeof item.image === "string")
      clean.image = safeUrl(item.image, origin);
    if (withDetails) {
      const opening = cleanOpening(item.opening);
      if (opening) clean.opening = opening;
    }
    return [clean];
  });
}

/** Mutations always read the latest value; snapshots are never written back. */
export function createFavoritesStore(getStorage, origin) {
  let state = { items: [], error: null };
  const listeners = new Set();
  function read() {
    try {
      const raw = getStorage().getItem(STORAGE_KEY);
      if (raw === null) return { items: [], error: null };
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        return { items: [], error: "corrupt" };
      }
      if (data?.version !== 2) return { items: [], error: "corrupt" };
      try {
        const items = normalizeItems(data.items, origin);
        return {
          items,
          error: items.length < data.items.length ? "repaired" : null,
        };
      } catch {
        return { items: [], error: "corrupt" };
      }
    } catch {
      return { items: state.items, error: "unavailable" };
    }
  }
  function publish(next) {
    if (
      next.error === state.error &&
      JSON.stringify(next.items) === JSON.stringify(state.items)
    )
      return state;
    state = next;
    for (const listener of listeners) listener(state);
    return state;
  }
  function refresh() {
    return publish(read());
  }
  function mutate(change) {
    const current = read();
    if (current.error === "corrupt" || current.error === "unavailable") {
      publish(current);
      return { ok: false, error: current.error };
    }
    const result = change(current.items);
    const items = normalizeItems(result.items, origin);
    try {
      getStorage().setItem(STORAGE_KEY, JSON.stringify({ version: 2, items }));
      publish({ items, error: null });
      return { ...result, ok: true, items };
    } catch {
      publish({ items: current.items, error: "write" });
      return { ok: false, error: "write" };
    }
  }
  function removeFrom(items, id) {
    const index = items.findIndex((item) => item.id === Number(id));
    const removed = items[index];
    const before = items[index + 1]?.id;
    return {
      items: items.filter((item) => item.id !== Number(id)),
      removed,
      before,
    };
  }
  const store = {
    refresh,
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    toggle(item) {
      const clean = normalizeItems([item], origin)[0];
      if (!clean) return { ok: false, error: "invalid" };
      return mutate((items) =>
        items.some((existing) => existing.id === clean.id)
          ? { ...removeFrom(items, clean.id), added: false }
          : { items: [...items, clean], added: true },
      );
    },
    remove: (id) => mutate((items) => removeFrom(items, id)),
    clear: () => mutate((items) => ({ items: [], removedItems: items })),
    restoreMany(incoming) {
      const saved = normalizeItems(incoming, origin);
      return mutate((items) => {
        const current = new Map(items.map((item) => [item.id, item]));
        const restored = saved.map((item) => {
          const latest = current.get(item.id) || item;
          current.delete(item.id);
          return latest;
        });
        return { items: [...restored, ...current.values()] };
      });
    },
    restore(item, before) {
      return mutate((items) => {
        if (items.some((existing) => existing.id === item.id)) return { items };
        const index = items.findIndex((existing) => existing.id === before);
        const next = [...items];
        next.splice(index < 0 ? next.length : index, 0, item);
        return { items: next };
      });
    },
    move(id, direction) {
      return mutate((items) => {
        const next = [...items];
        const index = next.findIndex((item) => item.id === Number(id));
        const target = index + direction;
        if (index >= 0 && target >= 0 && target < next.length)
          [next[index], next[target]] = [next[target], next[index]];
        return { items: next };
      });
    },
    moveBefore(id, before) {
      return mutate((items) => {
        const item = items.find((entry) => entry.id === Number(id));
        if (!item || Number(id) === Number(before)) return { items };
        const next = items.filter((entry) => entry.id !== item.id);
        const index = next.findIndex((entry) => entry.id === Number(before));
        next.splice(index < 0 ? next.length : index, 0, item);
        return { items: next };
      });
    },
    merge(incoming) {
      const clean = normalizeItems(incoming, origin);
      return mutate((items) => {
        const ids = new Set(items.map((item) => item.id));
        const added = clean.filter((item) => !ids.has(item.id));
        return { items: [...items, ...added], addedCount: added.length };
      });
    },
    updateDetails(incoming) {
      const current = read();
      const details = new Map(
        normalizeItems(incoming, origin).map((item) => [item.id, item]),
      );
      if (
        !current.items.some(
          (item) =>
            details.has(item.id) &&
            JSON.stringify({ ...item, ...details.get(item.id) }) !==
              JSON.stringify(item),
        )
      )
        return { ok: true };
      return mutate((items) => ({
        items: items.map((item) => ({ ...item, ...details.get(item.id) })),
      }));
    },
    reset() {
      try {
        getStorage().setItem(
          STORAGE_KEY,
          JSON.stringify({ version: 2, items: [] }),
        );
        publish({ items: [], error: null });
        return { ok: true };
      } catch {
        publish({ ...state, error: "unavailable" });
        return { ok: false, error: "unavailable" };
      }
    },
  };
  refresh();
  return store;
}

/** One store per page; other tabs report changes via the storage event. */
export function getFavoritesStore() {
  if (!window.weissenstadtFavoritesStore) {
    const store = createFavoritesStore(
      () => window.localStorage,
      window.location.origin,
    );
    window.weissenstadtFavoritesStore = store;
    window.addEventListener("storage", (event) => {
      if (event.key === STORAGE_KEY || event.key === null) store.refresh();
    });
    window.addEventListener("pageshow", () => store.refresh());
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) store.refresh();
    });
  }
  return window.weissenstadtFavoritesStore;
}
